import React from "react";
import { useTranslation } from "react-i18next";
import SkeletonBlock from "./SkeletonBlock";
import { numOf, topItem, sumValues, fmt, trendFor, pluralize, pctTotalKpi } from "./kpiHelpers";

// Single-attribute KPI strip (Clarity/Color/Cut/Shape/Certificate/Size/Clicks).
// Same edr-kpis/edr-kpi markup as ReportKpiBand so both report families read
// as one visual system, driven off a single attribute slice instead of the
// full multi-attribute Extended payload.
const ReportKpiStrip = ({ report, loading, config }) => {
  const { t } = useTranslation();

  if (loading) {
    const skeletonCount =
      3 + (config.hideTotalDimensionSum || config.percentOnly ? 0 : 1) + (config.noiseFilter ? 1 : 0) + (config.showTop3Concentration ? 1 : 0);
    return (
      <div className="edr-kpis">
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <SkeletonBlock className="edr-kpi-skeleton" key={index} />
        ))}
      </div>
    );
  }

  const totalClicksKey = config.totalClicksKey || "TotalDiamondClicks";
  const summaryKpiKey = config.summaryKpiKey || "DiamondSummaryKPIs";
  const totalClicks = report?.[totalClicksKey]?.[0]?.TotalClickCnt || 0;
  const list = report?.[config.dataKey] || [];
  // Noise-filtered reports (Cut Grade/Shape) bucket invalid values into an
  // "Other" row in `list` -- exclude it from every KPI except "Flagged /
  // Grouped" (below), which needs the raw list to count what got excluded.
  const validList = config.noiseFilter
    ? list.filter((item) => config.noiseFilter.isValid(item[config.labelField]))
    : list;
  const totalSearches = sumValues(validList, config.valueField);
  const top = topItem(validList, config.labelField, config.valueField);
  const distinctValues = validList.filter((item) => numOf(item[config.valueField]) > 0).length;

  const clicksTrend = trendFor(report?.[summaryKpiKey], "TotalClicks");

  // percentOnly (Global Diamond / Global Jewelry, Task 2344 - switch in
  // reports/_shared/globalReportSettings.js): every click figure is shown as
  // a share, never a raw count. Total Clicks is only the change vs the prior
  // period (see pctTotalKpi).
  const pctOnly = !!config.percentOnly;
  const summaryRows = report?.[summaryKpiKey] || [];
  // This page's own clicks now vs the prior period. Diamond's API sends one
  // row per attribute (Metric = the attribute's dataKey); Jewelry pages build
  // their TotalClicks row from this page's own data already.
  const clicksRow = summaryRows.find((row) => row.Metric === config.dataKey) || summaryRows.find((row) => row.Metric === "TotalClicks");
  const kpis = [
    pctOnly
      ? { lab: t("Total Clicks"), ...pctTotalKpi(clicksRow ? clicksRow.CurrentValue : totalClicks, clicksRow ? clicksRow.PriorValue : null) }
      : { lab: t("Total Clicks"), val: fmt(totalClicks), trend: clicksTrend },
  ];
  // "Total X Searches" is a raw count - hidden on percent-only reports.
  if (!config.hideTotalDimensionSum && !pctOnly) {
    kpis.push({ lab: t("Total {{dim}} Searches", { dim: t(config.dimensionLabel) }), val: fmt(totalSearches) });
  }
  kpis.push({
    lab: t("Top {{dim}}", { dim: t(config.dimensionLabel) }),
    val: top?.label,
    sub: top && (pctOnly ? `${top.percentage}% ${t("of clicks")}` : `${fmt(top.value)} · ${top.percentage}%`),
  });

  if (config.showTop3Concentration) {
    const top3Sum = [...validList]
      .sort((a, b) => numOf(b[config.valueField]) - numOf(a[config.valueField]))
      .slice(0, 3)
      .reduce((sum, item) => sum + numOf(item[config.valueField]), 0);
    // Percent-only reports show 2 decimals like every other share (Task 2344).
    const top3Pct = totalSearches > 0 ? (pctOnly ? ((top3Sum / totalSearches) * 100).toFixed(2) : Math.round((top3Sum / totalSearches) * 100)) : 0;
    kpis.push({
      lab: t("Top-3 Concentration"),
      val: `${top3Pct}%`,
      sub: t("of all clicks in top 3"),
    });
  }

  // Distinct Values = how many different values were searched (not a click
  // count) - shown on percent-only reports too (QA 2344).
  kpis.push({ lab: t("Distinct Values"), val: distinctValues, sub: t("unique {{dim}} searched", { dim: pluralize(t(config.dimensionLabel)?.toLowerCase()) }) });

  if (config.noiseFilter) {
    // flaggedOtherLabel (Global Metal Type): the card is the grouped "Other"
    // row's share, and only shows when that row has data (QA 2344).
    const isFlagged = (item) =>
      config.flaggedOtherLabel
        ? item[config.labelField] === config.flaggedOtherLabel
        : !config.noiseFilter.isValid(item[config.labelField]);
    const flaggedCount = list
      .filter(isFlagged)
      .reduce((sum, item) => sum + numOf(item[config.valueField]), 0);
    const rawTotal = sumValues(list, config.valueField);
    if (!config.flaggedOtherLabel || flaggedCount > 0) {
      kpis.push({
        lab: t("Flagged / Grouped"),
        val: pctOnly ? `${rawTotal > 0 ? ((flaggedCount / rawTotal) * 100).toFixed(2) : "0.00"}%` : fmt(flaggedCount),
        sub: t(config.flaggedOtherLabel ? config.flaggedOtherSub : "invalid values bucketed into Others"),
      });
    }
  }

  return (
    <div className="edr-kpis">
      {kpis.map((kpi, index) => (
        <div className="edr-kpi" key={index}>
          <div className="edr-kpi-lab">{kpi.lab}</div>
          <div className={`edr-kpi-val ${kpi.valClass || ""}`}>{kpi.val ?? "—"}</div>
          {kpi.sub && <div className="edr-kpi-sub">{kpi.sub}</div>}
          {kpi.trend && (
            <div className={`edr-kpi-trend ${kpi.trend.up ? "edr-up" : "edr-down"}`}>
              {kpi.trend.up ? "▲" : "▼"} {Math.abs(kpi.trend.change).toFixed(2)}% {t("vs prior period")}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ReportKpiStrip;
